import styled from "@emotion/styled";
import { Col, Row, Spin, Tooltip } from "antd";
import {
  CheckCircleOutlined,
  ClockCircleOutlined,
  DollarOutlined,
  FileExclamationOutlined,
  RightOutlined,
  SyncOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import { useGetOrderInProgressSummary, useGetOrderPendingPaymentSummary, useGetPayoutBatchSummary } from "hooks/useFactory";
import { numberFormatePrice } from "utils/formateNumber";
import { useState } from "react";

type ActiveCard = "pendingPayment" | "inProgress" | "batchSummary" | "completedSummary" | null;

interface FinanceSummaryProps {
  activeCard: ActiveCard;
  onCardClick: (card: ActiveCard) => void;
}

const FinanceSummary = ({ activeCard, onCardClick }: FinanceSummaryProps) => {
  const [startDate] = useState(
    dayjs().startOf("year").format("YYYY-MM-DD"),
  );
  const [endDate] = useState(dayjs().format("YYYY-MM-DD"));

  const { data: dataInProgressSummary, isLoading: loadingInProgress } =
    useGetOrderInProgressSummary({
      start_date: startDate,
      end_date: endDate,
    });

  const { data: dataPendingPaymentSummary, isLoading: loadingPendingPayment } =
    useGetOrderPendingPaymentSummary({});

  const { data: payoutBatchData, isLoading: loadingBatchSummary } =
    useGetPayoutBatchSummary();

  const inProgressSummary = dataInProgressSummary?.data;
  const pendingSummary = dataPendingPaymentSummary?.data;
  const payoutPending = payoutBatchData?.data.pending;
  const payoutPaid = payoutBatchData?.data.paid;

  const handleClick = (card: Exclude<ActiveCard, null>) => {
    onCardClick(activeCard === card ? null : card);
  };

  return (
    <Wrapper>
      <Row gutter={[16, 16]} style={{ alignItems: "stretch" }}>
        <Col xs={24} sm={12} xl={6} style={{ display: "flex" }}>
          <CardContainer>
            <CardWrapper
              accentColor="#8c8c8c"
              isActive={activeCard === "inProgress"}
              isClickable
              onClick={() => handleClick("inProgress")}
            >
              <CardTop>
                <IconCircle bgColor="#f5f5f5" color="#8c8c8c">
                  <SyncOutlined spin={loadingInProgress} />
                </IconCircle>
                <CardLabel>การซื้อขายที่กำลังดำเนินการ</CardLabel>
              </CardTop>
              {loadingInProgress ? (
                <SpinWrapper>
                  <Spin size="small" />
                </SpinWrapper>
              ) : (
                <>
                  <ValueText color="#595959">
                    {numberFormatePrice(inProgressSummary?.total_amount ?? 0)} บาท
                  </ValueText>
                  <SubText>{inProgressSummary?.total_count ?? 0} รายการ</SubText>
                </>
              )}
            </CardWrapper>
            <ArrowSeparator><RightOutlined /></ArrowSeparator>
          </CardContainer>
        </Col>

        <Col xs={24} sm={12} xl={6} style={{ display: "flex" }}>
          <CardContainer>
            <CardWrapper
              accentColor="#1d39c4"
              isActive={activeCard === "pendingPayment"}
              isClickable
              onClick={() => handleClick("pendingPayment")}
            >
              <CardTop>
                <IconCircle bgColor="#f0f5ff" color="#1d39c4">
                  <ClockCircleOutlined />
                </IconCircle>
                <CardLabel>รอกดยืนยันยอดโอน</CardLabel>
              </CardTop>
              {loadingPendingPayment ? (
                <SpinWrapper>
                  <Spin size="small" />
                </SpinWrapper>
              ) : (
                <>
                  <ValueText color="#1d39c4">
                    {numberFormatePrice(pendingSummary?.total_amount ?? 0)} บาท
                  </ValueText>
                  <SubText>{pendingSummary?.total_count ?? 0} รายการ</SubText>
                </>
              )}
            </CardWrapper>
            <ArrowSeparator><RightOutlined /></ArrowSeparator>
          </CardContainer>
        </Col>

        <Col xs={24} sm={12} xl={6} style={{ display: "flex" }}>
          <CardContainer>
            <CardWrapper
              accentColor="#722ed1"
              isActive={activeCard === "batchSummary"}
              isClickable
              onClick={() => handleClick("batchSummary")}
            >
              <CardTop>
                <IconCircle bgColor="#f9f0ff" color="#722ed1">
                  <DollarOutlined />
                </IconCircle>
                <CardLabel>รอการชำระเงิน</CardLabel>
              </CardTop>
              {loadingBatchSummary ? (
                <SpinWrapper>
                  <Spin size="small" />
                </SpinWrapper>
              ) : (
                <>
                  <ValueText color="#722ed1">
                    {(payoutPending?.batch_count ?? 0) + (payoutPaid?.no_receipt_count ?? 0)} รายการ
                  </ValueText>
                  <div style={{ display: "flex", flexDirection: "row", alignItems: "center", gap: 4, justifyContent: "space-between", width: "100%", }}>
                    <SubText>
                      {numberFormatePrice((payoutPending?.total_amount ?? 0) + (payoutPaid?.no_receipt_total_amount ?? 0))} บาท
                    </SubText>
                    {(payoutPaid?.no_receipt_count ?? 0) > 0 && (
                      <Tooltip title={`มี ${payoutPaid?.no_receipt_count} รายการที่โอนแล้วแต่ยังไม่อัพโหลดใบเสร็จ`}>
                        <NoReceiptBadge>
                          <FileExclamationOutlined />
                          รอใบเสร็จ {payoutPaid?.no_receipt_count} รายการ
                        </NoReceiptBadge>
                      </Tooltip>
                    )}
                  </div>

                </>
              )}
            </CardWrapper>
            <ArrowSeparator><RightOutlined /></ArrowSeparator>
          </CardContainer>
        </Col>

        <Col xs={24} sm={12} xl={6} style={{ display: "flex" }}>
          <CardWrapper
            accentColor="#52c41a"
            isActive={activeCard === "completedSummary"}
            isClickable
            onClick={() => handleClick("completedSummary")}
          >
            <CardTop>
              <IconCircle bgColor="#f6ffed" color="#52c41a">
                <CheckCircleOutlined />
              </IconCircle>
              <CardLabel>เสร็จสิ้น</CardLabel>
            </CardTop>
            {loadingBatchSummary ? (
              <SpinWrapper>
                <Spin size="small" />
              </SpinWrapper>
            ) : (
              <>
                <ValueText color="#52c41a">
                  {payoutPaid?.with_receipt_count ?? 0} รายการ
                </ValueText>
                <SubText>
                  {numberFormatePrice(payoutPaid?.with_receipt_total_amount ?? 0)} บาท
                </SubText>
              </>
            )}
          </CardWrapper>
        </Col>
      </Row>
    </Wrapper>
  );
};

export default FinanceSummary;

const Wrapper = styled.div`
  padding: 16px 20px;
`;

interface CardWrapperProps {
  accentColor: string;
  isActive: boolean;
  isClickable: boolean;
}

const CardWrapper = styled.div<CardWrapperProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  padding: 16px 20px;
  background-color: #ffffff;
  border-radius: 8px;
  border-top: 4px solid ${(props) => props.accentColor};
  position: relative;
  transition: box-shadow 0.2s, border 0.2s;
  cursor: ${(props) => (props.isClickable ? "pointer" : "default")};
  box-shadow: ${(props) =>
    props.isActive
      ? `0 0 0 2px ${props.accentColor}55`
      : "0px 2px 10px rgba(0, 0, 0, 0.08)"};
  outline: ${(props) =>
    props.isActive ? `2px solid ${props.accentColor}` : "2px solid transparent"};

  ${(props) =>
    props.isClickable &&
    `
    &:hover {
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.14);
    }
  `}
`;

const CardTop = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 10px;
  margin-bottom: 8px;
`;

interface IconCircleProps {
  bgColor: string;
  color: string;
}

const IconCircle = styled.div<IconCircleProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  min-width: 36px;
  border-radius: 50%;
  background-color: ${(props) => props.bgColor};
  color: ${(props) => props.color};
  font-size: 18px;
`;

const CardLabel = styled.p`
  font-size: 13px;
  font-weight: 500;
  color: #595959;
  margin: 0;
  line-height: 1.4;
`;

interface ValueTextProps {
  color: string;
}

const ValueText = styled.p<ValueTextProps>`
  font-size: 26px;
  font-weight: 700;
  color: ${(props) => props.color};
  margin: 0 0 2px 0;
  line-height: 1.2;
`;

const SubText = styled.p`
  font-size: 13px;
  color: #8c8c8c;
  margin: 0;
`;

const SpinWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 1;
  min-height: 60px;
`;

const CardContainer = styled.div`
  position: relative;
  display: flex;
  width: 100%;
`;

const ArrowSeparator = styled.div`
  position: absolute;
  right: -12px;
  top: 50%;
  transform: translateY(-50%);
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  background: #ffffff;
  border-radius: 50%;
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
  color: #8c8c8c;
  font-size: 10px;

  @media (max-width: 1199px) {
    display: none;
  }
`;

const NoReceiptBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  margin-top: 8px;
  padding: 2px 8px;
  background-color: #fff7e6;
  border: 1px solid #ffd591;
  border-radius: 4px;
  color: #d46b08;
  font-size: 12px;
  font-weight: 500;
  cursor: default;
`;

